import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BadgeCheck, Ban, IdCard, Layers } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, BulkAction, RowAction, Selection } from '@/components/DataTable/types'
import { fetchGroups, type AdminGroupRow } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { GROUP_OPS_STR as S } from './groupOpsApi'
import { setGroupBlocked, setGroupVerified } from './groupVerifyApi'

/**
 * A4.03 — Ommaviy amallar. JONLI `/admin/groups` ro'yxati; belgilangan qatorlarga
 * ommaviy blok / verifikatsiya (haqiqiy `/admin/groups/:id/block|verify` endpoint-
 * lari bo'yicha aylanadi). «Filtr bo'yicha hammasi» rejimi qo'llab-quvvatlanmaydi.
 */
export function GroupBulkActions() {
  const t = useT()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [reloadKey, setReloadKey] = useState(0)

  const runBulk = useCallback(
    async (sel: Selection<AdminGroupRow>, op: 'block' | 'verify') => {
      if (sel.mode !== 'rows') {
        toast(S.selectRowsFirst, { tone: 'warning' })
        return
      }
      const rows = sel.rows
      if (rows.length === 0) {
        toast(S.selectRowsFirst, { tone: 'warning' })
        return
      }
      if (!window.confirm(`${op === 'block' ? S.bulkBlock : S.bulkVerify} (${rows.length})?`)) return
      const results = await Promise.allSettled(
        rows.map((r) =>
          op === 'block' ? setGroupBlocked(r.id, true, '') : setGroupVerified(r.id, true),
        ),
      )
      const ok = results.filter((x) => x.status === 'fulfilled').length
      const failed = results.length - ok
      toast(failed === 0 ? `${S.bulkDone} (${ok})` : `${S.bulkPartial}: ${ok}/${results.length}`, {
        tone: failed === 0 ? 'success' : 'warning',
      })
      setReloadKey((k) => k + 1)
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<AdminGroupRow>[]>(
    () => [
      {
        id: 'card',
        label: t.users.viewCard,
        icon: <IdCard size={13} />,
        onSelect: (r) => navigate(`/groups/${r.id}`),
      },
    ],
    [t, navigate],
  )

  const bulkActions = useMemo<BulkAction<AdminGroupRow>[]>(
    () => [
      {
        id: 'bulk-block',
        label: S.bulkBlock,
        icon: <Ban size={13} />,
        danger: true,
        permission: PERMISSIONS.USERS_BAN,
        onSelect: (sel) => void runBulk(sel, 'block'),
      },
      {
        id: 'bulk-verify',
        label: S.bulkVerify,
        icon: <BadgeCheck size={13} />,
        permission: PERMISSIONS.USERS_BAN,
        onSelect: (sel) => void runBulk(sel, 'verify'),
      },
    ],
    [runBulk],
  )

  const columns = useMemo<AdminColumn<AdminGroupRow>[]>(
    () => [
      {
        key: 'type',
        header: t.col.type,
        type: 'enum',
        width: 110,
        options: [
          { value: 'group', label: t.groups.group, status: 'active' },
          { value: 'channel', label: t.groups.channel, status: 'review' },
        ],
      },
      { key: 'title', header: t.col.title, type: 'text', minWidth: 200 },
      { key: 'username', header: t.col.username, type: 'text', width: 160 },
      { key: 'member_count', header: t.groups.members, type: 'number', width: 110 },
      { key: 'is_forum', header: t.groups.forum, type: 'bool', width: 90 },
      { key: 'created_at', header: t.col.registered, type: 'date', width: 150 },
    ],
    [t],
  )

  const fetcher = useCallback(fetchGroups, [])

  return (
    <>
      <PageHeader title={S.bulkActions} screenId="A4.03" description={S.bulkActionsNote} />
      <DataTable<AdminGroupRow>
        key={reloadKey}
        screenId="A4.03"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        selectable
        bulkActions={bulkActions}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Layers size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
